import React, { useState, useEffect } from 'react';
import { Settings, DollarSign, Clock, Calendar, Gift, Save, RefreshCw, AlertCircle, Users, Shield, CheckCircle, XCircle, Search, Filter } from 'lucide-react';
import { database } from '../../firebase';
import { ref, set, onValue, update, get } from 'firebase/database';

const WithdrawalSetting = () => {
  const [activeTab, setActiveTab] = useState('conditions');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const [userList, setUserList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [conditions, setConditions] = useState({
    minWithdrawal: 12,
    agPower: {
      days: ['Everyday'],
      startTime1: '00:00',
      endTime1: '03:00',
      slot1Enabled: true,
      startTime2: '12:00',
      endTime2: '15:00',
      slot2Enabled: true
    },
    agRoi: {
      days: ['Wednesday'],
      startTime1: '00:00',
      endTime1: '03:00',
      slot1Enabled: true,
      startTime2: '12:00',
      endTime2: '15:00',
      slot2Enabled: true
    },
    agEnergy: {
      days: ['Saturday'],
      startTime1: '00:00',
      endTime1: '03:00',
      slot1Enabled: true,
      startTime2: '12:00',
      endTime2: '15:00',
      slot2Enabled: true
    }
  });

  const [packages, setPackages] = useState({});
  const [fees, setFees] = useState({});

  // Dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [dialogData, setDialogData] = useState(null);

  const normalizeSchedule = (schedule, fallback) => ({
    days: schedule?.days || fallback.days,
    startTime1: schedule?.startTime1 ?? fallback.startTime1,
    endTime1: schedule?.endTime1 ?? fallback.endTime1,
    slot1Enabled: schedule?.slot1Enabled ?? true,
    startTime2: schedule?.startTime2 ?? fallback.startTime2,
    endTime2: schedule?.endTime2 ?? fallback.endTime2,
    slot2Enabled: schedule?.slot2Enabled ?? true
  });

  const normalizeConditions = (data) => ({
    minWithdrawal: data?.minWithdrawal ?? 12,
    agPower: normalizeSchedule(data?.agPower || data?.energy, conditions.agPower),
    agRoi: normalizeSchedule(data?.agRoi || data?.roi, conditions.agRoi),
    agEnergy: normalizeSchedule(data?.agEnergy || data?.reward, conditions.agEnergy)
  });

  useEffect(() => {
    fetchData();
    
    // Real-time listeners
    const conditionsRef = ref(database, 'withdrawalConditions');
    const packagesRef = ref(database, 'Packages');
    
    const unsubscribeConditions = onValue(conditionsRef, (snapshot) => {
      if (snapshot.exists()) {
        setConditions(normalizeConditions(snapshot.val()));
      }
    });
    
    const unsubscribePackages = onValue(packagesRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setPackages(data);
        
        // Extract fees
        const feesData = {};
        Object.keys(data).forEach(pkgName => {
          feesData[pkgName] = data[pkgName]?.withdrawalFee || 10;
        });
        setFees(feesData);
      }
    });
    
    return () => {
      unsubscribeConditions();
      unsubscribePackages();
    };
  }, []);

  const [aestNow, setAestNow] = useState('');
  useEffect(() => {
    const formatter = new Intl.DateTimeFormat('en-AU', {
      timeZone: 'Asia/Dubai',
      dateStyle: 'full',
      timeStyle: 'short'
    });
    const updateClock = () => {
      setAestNow(formatter.format(new Date()));
    };
    updateClock();
    const id = setInterval(updateClock, 1000);
    return () => clearInterval(id);
  }, []);

  const fetchData = async () => {
    try {
      setMessage({ type: 'info', text: 'Loading data...' });
      
      const [conditionsSnapshot, packagesSnapshot] = await Promise.all([
        get(ref(database, 'withdrawalConditions')),
        get(ref(database, 'Packages'))
      ]);
      
      if (conditionsSnapshot.exists()) {
        setConditions(normalizeConditions(conditionsSnapshot.val()));
      }
      
      if (packagesSnapshot.exists()) {
        const packagesData = packagesSnapshot.val();
        setPackages(packagesData);
        
        const feesData = {};
        Object.keys(packagesData).forEach(pkgName => {
          feesData[pkgName] = packagesData[pkgName]?.withdrawalFee || 10;
        });
        setFees(feesData);
      }
      
      // Fetch users with date-wise structure
      await fetchUsersData();
      
      setMessage({ type: 'success', text: 'Data loaded successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 2000);
    } catch (error) {
      console.error('Error fetching data:', error);
      setMessage({ type: 'error', text: 'Failed to load data' });
    }
  };

  const fetchUsersData = async () => {
    try {
      const usersRef = ref(database, 'users');
      const snapshot = await get(usersRef);
      
      if (snapshot.exists()) {
        const usersData = snapshot.val();
        const usersArray = [];
        
        // Iterate through date folders
        Object.entries(usersData).forEach(([date, dateUsers]) => {
          Object.entries(dateUsers).forEach(([dbKey, user]) => {
            usersArray.push({
              dbKey,
              date: date, // Store the date folder
              userId: user.sponsorId || dbKey,
              name: user.name || 'Unknown',
              email: user.email || '',
              phone: user.phone || '',
              status: user.status || 'active',
              withdrawalStatus: user.withdrawalStatus || 'active',
              lastLogin: user.lastLogin || '',
              registrationDate: user.registrationDate || date,
              wallets: user.wallets || {},
              totalEarnings: user.totalEarnings || 0,
              totalWithdrawal: user.totalWithdrawal || 0,
              packages: user.packages || []
            });
          });
        });
        
        setUserList(usersArray);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  };

  // Show confirmation dialog
  const showConfirmationDialog = (user, action) => {
    setDialogData({
      user,
      action,
      title: getActionTitle(action),
      message: getActionMessage(user, action)
    });
    setShowDialog(true);
  };

  const getActionTitle = (action) => {
    switch(action) {
      case 'pause': return 'Pause Withdrawal';
      case 'resume': return 'Resume Withdrawal';
      case 'block': return 'Block Withdrawal';
      case 'unblock': return 'Unblock Withdrawal';
      default: return 'Confirm Action';
    }
  };

  const getActionMessage = (user, action) => {
    const name = user.name || 'Unknown';
    switch(action) {
      case 'pause': return `Are you sure you want to pause withdrawal for ${name}?`;
      case 'resume': return `Are you sure you want to resume withdrawal for ${name}?`;
      case 'block': return `Are you sure you want to block withdrawal for ${name}?`;
      case 'unblock': return `Are you sure you want to unblock withdrawal for ${name}?`;
      default: return 'Are you sure?';
    }
  };

  // Update user withdrawal status
  const updateUserWithdrawalStatus = async () => {
    if (!dialogData) return;
    
    const { user, action } = dialogData;
    let newStatus = user.withdrawalStatus;
    
    switch(action) {
      case 'pause':
        newStatus = 'paused';
        break;
      case 'resume':
        newStatus = 'active';
        break;
      case 'block':
        newStatus = 'blocked';
        break;
      case 'unblock':
        newStatus = 'active';
        break;
    }
    
    try {
      // Update in date-wise structure: users/date/dbKey
      const userRef = ref(database, `users/${user.date}/${user.dbKey}`);
      
      await update(userRef, {
        withdrawalStatus: newStatus,
        withdrawalStatusUpdated: new Date().toISOString(),
        withdrawalUpdatedBy: 'admin'
      });
      
      // Create admin action record
      const actionId = `WD${Date.now()}`;
      const actionDate = new Date().toISOString().split('T')[0];
      const actionRef = ref(database, `adminWithdrawalActions/${actionDate}/${actionId}`);
      
      await set(actionRef, {
        id: actionId,
        userId: user.userId,
        userName: user.name,
        action: action,
        previousStatus: user.withdrawalStatus,
        newStatus: newStatus,
        adminId: 'admin',
        timestamp: new Date().toISOString(),
        note: `${action} withdrawal for user ${user.name}`
      });
      
      // Update local state
      setUserList(prev => prev.map(u => 
        u.dbKey === user.dbKey && u.date === user.date
          ? { ...u, withdrawalStatus: newStatus }
          : u
      ));
      
      // Show success message
      setMessage({ 
        type: 'success', 
        text: `Withdrawal ${action} for ${user.name}!` 
      });
      
    } catch (error) {
      console.error('Error updating withdrawal status:', error);
      setMessage({ 
        type: 'error', 
        text: 'Failed to update withdrawal status' 
      });
    } finally {
      setShowDialog(false);
      setDialogData(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    
    try {
      if (activeTab === 'conditions') {
        await set(ref(database, 'withdrawalConditions'), conditions);
        setMessage({ type: 'success', text: 'Conditions saved successfully!' });
      } else if (activeTab === 'fees') {
        const updatePromises = Object.keys(fees).map(async (pkgName) => {
          const packageRef = ref(database, `Packages/${pkgName}`);
          await update(packageRef, {
            withdrawalFee: fees[pkgName]
          });
        });
        
        await Promise.all(updatePromises);
        setMessage({ type: 'success', text: 'Fees updated successfully!' });
      }
      
      setTimeout(() => setMessage({ type: '', text: '' }), 2000);
    } catch (error) {
      console.error('Error saving:', error);
      setMessage({ type: 'error', text: 'Save failed. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleConditionChange = (type, field, value) => {
    setConditions(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value
      }
    }));
  };

  const handleDaysChange = (type, day) => {
    setConditions(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        days: prev[type].days.includes(day) 
          ? prev[type].days.filter(d => d !== day)
          : [...prev[type].days, day]
      }
    }));
  };

  const handleSlotToggle = (type, field) => {
    setConditions(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: !prev[type][field]
      }
    }));
  };

  const handleFeeChange = (pkgName, value) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
      setFees(prev => ({
        ...prev,
        [pkgName]: numValue
      }));
    }
  };

  // Filter users based on search
  const filteredUsers = userList.filter(user => 
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.userId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Count users by withdrawal status
  const activeUsers = userList.filter(u => u.withdrawalStatus === 'active').length;
  const pausedUsers = userList.filter(u => u.withdrawalStatus === 'paused').length;
  const blockedUsers = userList.filter(u => u.withdrawalStatus === 'blocked').length;

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const TIMEZONE_LABEL = 'GST (Asia/Dubai)';

  // Withdrawal Type Section Component
  const WithdrawalTypeSection = ({ type, title, icon: Icon }) => (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Icon className="text-blue-400" size={20} />
        <h3 className="text-lg font-bold text-white">{title}</h3>
      </div>
      
      <div className="bg-gray-800/30 rounded-lg p-3 border border-gray-700">
        <label className="block text-gray-300 mb-2">Available Days</label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {type === 'agPower' && (
            <button
              onClick={() => handleDaysChange(type, 'Everyday')}
              className={`py-2 rounded text-sm transition-colors ${conditions[type].days.includes('Everyday')
                ? 'bg-blue-600 text-white border border-blue-500' 
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-600'}`}
            >
              Everyday
            </button>
          )}
          {daysOfWeek.map(day => (
            <button
              key={day}
              onClick={() => handleDaysChange(type, day)}
              className={`py-2 rounded text-sm transition-colors ${conditions[type].days.includes(day)
                ? 'bg-blue-600 text-white border border-blue-500' 
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-600'}`}
            >
              {day.substring(0, 3)}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-gray-300 text-sm">First Time Slot</h4>
            <button
              type="button"
              onClick={() => handleSlotToggle(type, 'slot1Enabled')}
              className={`w-12 h-6 rounded-full relative transition-colors flex-shrink-0 ${
                conditions[type].slot1Enabled ? 'bg-green-500' : 'bg-gray-600'
              }`}
            >
              <span
                className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                  conditions[type].slot1Enabled ? 'transform translate-x-6' : ''
                }`}
              />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-gray-400 text-xs mb-1">Start</label>
              <input
                type="time"
                value={conditions[type].startTime1}
                onChange={(e) => handleConditionChange(type, 'startTime1', e.target.value)}
                disabled={!conditions[type].slot1Enabled}
                className={`w-full px-2 py-1.5 border rounded text-white text-sm ${
                  conditions[type].slot1Enabled ? 'bg-gray-800 border-gray-600' : 'bg-gray-900 border-gray-700 text-gray-500 cursor-not-allowed'
                }`}
              />
            </div>
            <div>
              <label className="block text-gray-400 text-xs mb-1">End</label>
              <input
                type="time"
                value={conditions[type].endTime1}
                onChange={(e) => handleConditionChange(type, 'endTime1', e.target.value)}
                disabled={!conditions[type].slot1Enabled}
                className={`w-full px-2 py-1.5 border rounded text-white text-sm ${
                  conditions[type].slot1Enabled ? 'bg-gray-800 border-gray-600' : 'bg-gray-900 border-gray-700 text-gray-500 cursor-not-allowed'
                }`}
              />
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-gray-300 text-sm">Second Time Slot</h4>
            <button
              type="button"
              onClick={() => handleSlotToggle(type, 'slot2Enabled')}
              className={`w-12 h-6 rounded-full relative transition-colors flex-shrink-0 ${
                conditions[type].slot2Enabled ? 'bg-green-500' : 'bg-gray-600'
              }`}
            >
              <span
                className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                  conditions[type].slot2Enabled ? 'transform translate-x-6' : ''
                }`}
              />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-gray-400 text-xs mb-1">Start</label>
              <input
                type="time"
                value={conditions[type].startTime2}
                onChange={(e) => handleConditionChange(type, 'startTime2', e.target.value)}
                disabled={!conditions[type].slot2Enabled}
                className={`w-full px-2 py-1.5 border rounded text-white text-sm ${
                  conditions[type].slot2Enabled ? 'bg-gray-800 border-gray-600' : 'bg-gray-900 border-gray-700 text-gray-500 cursor-not-allowed'
                }`}
              />
            </div>
            <div>
              <label className="block text-gray-400 text-xs mb-1">End</label>
              <input
                type="time"
                value={conditions[type].endTime2}
                onChange={(e) => handleConditionChange(type, 'endTime2', e.target.value)}
                disabled={!conditions[type].slot2Enabled}
                className={`w-full px-2 py-1.5 border rounded text-white text-sm ${
                  conditions[type].slot2Enabled ? 'bg-gray-800 border-gray-600' : 'bg-gray-900 border-gray-700 text-gray-500 cursor-not-allowed'
                }`}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Package Fee Card Component
  const PackageFeeCard = ({ pkgName, fee }) => (
    <div className="bg-gray-800/30 rounded-lg p-3 border border-gray-700 hover:border-gray-600 transition-colors">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="font-bold text-white">{pkgName}</h4>
          <p className="text-xs text-gray-400">Withdrawal Fee</p>
        </div>
        <span className="text-xl font-bold text-blue-400">{fee}%</span>
      </div>
      
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <input
            type="range"
            min="0"
            max="50"
            step="0.5"
            value={fee}
            onChange={(e) => handleFeeChange(pkgName, e.target.value)}
            className="flex-1 h-2 bg-gray-700 rounded cursor-pointer"
          />
          <input
            type="number"
            value={fee}
            onChange={(e) => handleFeeChange(pkgName, e.target.value)}
            min="0"
            max="100"
            step="0.1"
            className="w-14 px-2 py-1 bg-gray-800 border border-gray-600 rounded text-white text-sm"
          />
        </div>
      </div>
    </div>
  );

  // Confirmation Dialog Component
  const ConfirmationDialog = () => {
    if (!showDialog || !dialogData) return null;

    const { user, action, title, message } = dialogData;
    const isBlockAction = action === 'block' || action === 'unblock';
    const isPauseAction = action === 'pause' || action === 'resume';

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
        <div className="bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl max-w-md w-full">
          <div className="flex items-center justify-between p-6 border-b border-gray-800">
            <h3 className="text-xl font-bold text-white">{title}</h3>
            <button
              onClick={() => setShowDialog(false)}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <XCircle size={24} />
            </button>
          </div>
          
          <div className="p-6">
            <div className="text-center mb-6">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                isBlockAction ? 'bg-red-500/20' : 'bg-yellow-500/20'
              }`}>
                {isBlockAction ? (
                  <Shield className="text-red-400" size={32} />
                ) : (
                  <Clock className="text-yellow-400" size={32} />
                )}
              </div>
              <h4 className="text-lg font-bold text-white mb-2">{title}</h4>
              <p className="text-gray-300">{message}</p>
            </div>
            
            <div className="bg-gray-800/50 rounded-xl p-4 mb-6">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-400">User:</span>
                  <span className="text-white font-bold">{user.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">User ID:</span>
                  <span className="text-white">{user.userId}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Current Status:</span>
                  <span className={`${
                    user.withdrawalStatus === 'active' ? 'text-green-400' :
                    user.withdrawalStatus === 'paused' ? 'text-yellow-400' :
                    'text-red-400'
                  }`}>
                    {user.withdrawalStatus}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex gap-4">
              <button
                onClick={() => setShowDialog(false)}
                className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 rounded-xl text-white font-bold transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={updateUserWithdrawalStatus}
                className={`flex-1 py-3 rounded-xl text-white font-bold transition-colors ${
                  isBlockAction ? 'bg-red-500 hover:bg-red-600' : 'bg-yellow-500 hover:bg-yellow-600'
                }`}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4">
      <h1 className="text-2xl font-bold text-white mb-2">Withdrawal Settings</h1>
      <p className="text-gray-400 mb-6">Configure withdrawal rules, fees & manage user permissions</p>

      {/* Message Alert */}
      {message.text && (
        <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
          message.type === 'success' ? 'bg-green-900/30 text-green-400 border border-green-700/30' : 
          message.type === 'error' ? 'bg-red-900/30 text-red-400 border border-red-700/30' : 
          'bg-blue-900/30 text-blue-400 border border-blue-700/30'
        }`}>
          <AlertCircle size={16} />
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-800 mb-6">
        <button 
          onClick={() => setActiveTab('conditions')} 
          className={`px-4 py-2 font-medium ${activeTab === 'conditions' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400 hover:text-gray-300'}`}
        >
          Conditions
        </button>
        <button 
          onClick={() => setActiveTab('fees')} 
          className={`px-4 py-2 font-medium ${activeTab === 'fees' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400 hover:text-gray-300'}`}
        >
          Package Fees
        </button>
        <button 
          onClick={() => setActiveTab('users')} 
          className={`px-4 py-2 font-medium ${activeTab === 'users' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400 hover:text-gray-300'}`}
        >
          User Status ({userList.length})
        </button>
      </div>

      {/* Content */}
      <div className="bg-gray-900/30 rounded-xl p-4 md:p-6 border border-gray-800">
        {activeTab === 'conditions' ? (
          <div className="space-y-6">
            {/* Minimum Withdrawal */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <DollarSign className="text-green-400" size={20} />
                <h3 className="font-bold text-white">Minimum Withdrawal</h3>
              </div>
              <div className="flex items-center gap-4">
                <input
                  type="number"
                  value={conditions.minWithdrawal}
                  onChange={(e) => setConditions(prev => ({...prev, minWithdrawal: parseFloat(e.target.value) || 0}))}
                  min="0"
                  step="0.01"
                  className="px-4 py-2 w-32 bg-gray-800 border border-gray-600 rounded-lg text-white"
                />
                <span className="text-gray-400 text-sm">Minimum withdrawal amount in USD</span>
              </div>
            </div>

            {/* Timezone Note */}
            <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-3 text-sm text-blue-200 flex flex-col md:flex-row md:items-center md:justify-between gap-1">
              <div>Timezone: {TIMEZONE_LABEL}. Please enter all withdrawal timing in GST.</div>
              <div className="text-blue-100">Current GST: {aestNow || 'Loading...'}</div>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-800 my-4"></div>

            {/* Withdrawal Types */}
              <div className="space-y-6">
                <WithdrawalTypeSection type="agPower" title="AV Power Withdrawal" icon={Gift} />
                <WithdrawalTypeSection type="agRoi" title="AV ROI Withdrawal" icon={Calendar} />
                <WithdrawalTypeSection type="agEnergy" title="AV Energy Withdrawal" icon={Gift} />
              </div>
          </div>
        ) : activeTab === 'fees' ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <DollarSign className="text-green-400" size={20} />
              <div>
                <h3 className="font-bold text-white">Package Fees</h3>
                <p className="text-gray-400 text-sm">Set withdrawal fee percentage for each package</p>
              </div>
            </div>

            {Object.keys(fees).length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-400">No packages found</p>
                <button
                  onClick={fetchData}
                  className="mt-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded text-sm"
                >
                  Reload Packages
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.keys(fees).map(pkgName => (
                  <PackageFeeCard key={pkgName} pkgName={pkgName} fee={fees[pkgName]} />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <Users className="text-blue-400" size={20} />
              <div>
                <h3 className="font-bold text-white">User Withdrawal Status</h3>
                <p className="text-gray-400 text-sm">Manage user withdrawal permissions and restrictions</p>
              </div>
            </div>

            {/* Status Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-green-900/20 border border-green-700/30 rounded-xl p-4 text-center">
                <div className="text-sm text-green-300 mb-1">Active Users</div>
                <div className="text-2xl font-bold text-green-400">{activeUsers}</div>
                <div className="text-xs text-green-400/70 mt-1">Can withdraw</div>
              </div>
              <div className="bg-yellow-900/20 border border-yellow-700/30 rounded-xl p-4 text-center">
                <div className="text-sm text-yellow-300 mb-1">Paused Users</div>
                <div className="text-2xl font-bold text-yellow-400">{pausedUsers}</div>
                <div className="text-xs text-yellow-400/70 mt-1">Withdrawal paused</div>
              </div>
              <div className="bg-red-900/20 border border-red-700/30 rounded-xl p-4 text-center">
                <div className="text-sm text-red-300 mb-1">Blocked Users</div>
                <div className="text-2xl font-bold text-red-400">{blockedUsers}</div>
                <div className="text-xs text-red-400/70 mt-1">Withdrawal blocked</div>
              </div>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search users by name, ID, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 pl-10 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500"
              />
            </div>

            {/* User List */}
            <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
              {filteredUsers.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Users className="mx-auto mb-2" size={32} />
                  <p>No users found</p>
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="mt-2 text-sm text-blue-400 hover:text-blue-300"
                    >
                      Clear search
                    </button>
                  )}
                </div>
              ) : (
                filteredUsers.map(user => {
                  const getStatusColor = () => {
                    switch(user.withdrawalStatus) {
                      case 'active': return 'bg-green-500/20 text-green-400 border-green-500/30';
                      case 'paused': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
                      case 'blocked': return 'bg-red-500/20 text-red-400 border-red-500/30';
                      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
                    }
                  };

                  return (
                    <div key={`${user.date}-${user.dbKey}`} className="bg-gray-800/30 rounded-xl p-4 border border-gray-700 hover:border-gray-600 transition-colors">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <div className="font-bold text-white">{user.name}</div>
                            <span className={`px-2 py-0.5 rounded text-xs border ${getStatusColor()}`}>
                              {user.withdrawalStatus}
                            </span>
                          </div>
                          <div className="text-sm text-gray-400 space-y-1">
                            <div>ID: {user.userId}</div>
                            {user.email && <div>Email: {user.email}</div>}
                            {user.phone && <div>Phone: {user.phone}</div>}
                            <div className="text-xs text-gray-500">Registered: {user.registrationDate}</div>
                          </div>
                        </div>
                        
                        <div className="flex gap-2">
                          {user.withdrawalStatus === 'blocked' ? (
                            <button
                              onClick={() => showConfirmationDialog(user, 'unblock')}
                              className="px-4 py-1.5 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg text-sm flex items-center gap-1 transition-colors"
                            >
                              <CheckCircle size={14} /> Unblock
                            </button>
                          ) : user.withdrawalStatus === 'paused' ? (
                            <button
                              onClick={() => showConfirmationDialog(user, 'resume')}
                              className="px-4 py-1.5 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg text-sm flex items-center gap-1 transition-colors"
                            >
                              <CheckCircle size={14} /> Resume
                            </button>
                          ) : (
                            <>
                              <button
                                onClick={() => showConfirmationDialog(user, 'pause')}
                                className="px-4 py-1.5 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 rounded-lg text-sm flex items-center gap-1 transition-colors"
                              >
                                <Clock size={14} /> Pause
                              </button>
                              <button
                                onClick={() => showConfirmationDialog(user, 'block')}
                                className="px-4 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-sm flex items-center gap-1 transition-colors"
                              >
                                <Shield size={14} /> Block
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      
                      <div className="pt-3 border-t border-gray-700 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div>
                          <div className="text-gray-400">Total Earnings</div>
                          <div className="text-green-400 font-bold">${(user.totalEarnings || 0).toFixed(2)}</div>
                        </div>
                        <div>
                          <div className="text-gray-400">Total Withdrawal</div>
                          <div className="text-blue-400 font-bold">${(user.totalWithdrawal || 0).toFixed(2)}</div>
                        </div>
                        <div>
                          <div className="text-gray-400">Packages</div>
                          <div className="text-white">{(user.packages || []).length}</div>
                        </div>
                        <div>
                          <div className="text-gray-400">Account Status</div>
                          <div className={`${user.status === 'active' ? 'text-green-400' : 'text-red-400'}`}>
                            {user.status}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 mt-6">
        <button
          onClick={fetchData}
          className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg font-medium flex items-center gap-2 transition-colors"
        >
          <RefreshCw size={16} /> Refresh
        </button>
        {activeTab !== 'users' && (
          <button
            onClick={handleSave}
            disabled={saving || (activeTab === 'fees' && Object.keys(fees).length === 0)}
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save size={16} />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        )}
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog />
    </div>
  );
};

export default WithdrawalSetting;

